// Phase A.4 / 8C.6 — Chain status card rendered inline in chat.
// Upgraded to use SIKA design tokens and premium plan-card layout.

import { Icon } from "../../../components/icons/Icon.tsx";

interface ChainData {
  id?: string;
  status?: string;
  current_week?: number | null;
  current_value?: number | null;
  opening_stake?: number | null;
  odds_target?: number | null;
  total_rollovers?: number | null;
  currency?: string;
  sika_goal_id?: string | null;
  outcome?: string;
  bust_protocol?: string;
  new_value?: number;
  completed?: boolean;
}

function fmtAmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

const STATUS_MAP: Record<string, { color: string; icon: string; label: string }> = {
  active: { color: "var(--sika-positive, #27AE60)", icon: "check", label: "Active" },
  pending_first: { color: "var(--sika-accent, #C5A028)", icon: "clock", label: "Pending" },
  paused: { color: "var(--sika-text-muted, #606060)", icon: "pause", label: "Paused" },
  busted: { color: "var(--sika-negative, #C0392B)", icon: "alert", label: "Busted" },
  cancelled: { color: "var(--sika-negative, #C0392B)", icon: "close", label: "Cancelled" },
  complete: { color: "var(--sika-positive, #27AE60)", icon: "trophy", label: "Complete" },
};

function ChainProgressBar({ week, total }: { week: number; total: number }): JSX.Element {
  const pct = total > 0 ? Math.min(100, Math.round((week / total) * 100)) : 0;
  return (
    <div className="ctx-progress-bar" style={{ marginTop: 8 }}>
      <div className="ctx-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

function SingleChain({ c }: { c: ChainData }): JSX.Element {
  const st = STATUS_MAP[c.status ?? ""] ?? { color: "var(--sika-text-secondary)", icon: "check", label: c.status ?? "Unknown" };
  const week = c.current_week ?? 0;
  const total = c.total_rollovers ?? 0;
  const value = Number(c.current_value ?? 0);
  const stake = Number(c.opening_stake ?? 0);
  const growth = stake > 0 ? ((value - stake) / stake * 100).toFixed(0) : "0";

  return (
    <div className="plan-stats">
      <div className="stat-row">
        <span className="lbl">
          <Icon name={st.icon} size={12} color={st.color} />
          Status
        </span>
        <span className="val" style={{ color: st.color, fontWeight: 600 }}>{st.label}</span>
      </div>
      <div className="stat-row">
        <span className="lbl">Progress</span>
        <span className="val">Week {week} of {total}</span>
      </div>
      <ChainProgressBar week={week} total={total} />
      <div className="stat-row">
        <span className="lbl">Current value</span>
        <span className="val">{c.currency === "GHS" ? "GH₵" : (c.currency ?? "GH₵")} {fmtAmt(c.current_value)}</span>
      </div>
      <div className="stat-row">
        <span className="lbl">Opening stake</span>
        <span className="val">{c.currency === "GHS" ? "GH₵" : (c.currency ?? "GH₵")} {fmtAmt(c.opening_stake)}</span>
      </div>
      {c.odds_target != null && (
        <div className="stat-row">
          <span className="lbl">Target odds</span>
          <span className="val">{Number(c.odds_target).toFixed(1)}x</span>
        </div>
      )}
      {value > stake && (
        <div className="stat-row">
          <span className="lbl">Growth</span>
          <span className="val" style={{ color: "var(--sika-positive, #27AE60)" }}>+{growth}%</span>
        </div>
      )}
    </div>
  );
}

export function SikaChainStatusCard({ data }: { data: unknown }): JSX.Element {
  // Outcome result (from record_outcome tool)
  if (data && typeof data === "object" && "outcome" in data) {
    const d = data as ChainData;
    const isWin = d.outcome === "win";
    return (
      <div className="plan-card" style={{
        borderColor: isWin ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
      }}>
        <div className="plan-head">
          <div className="label">{isWin ? "Win recorded" : "Loss recorded"}</div>
          <div className="ttl" style={{
            fontFamily: "var(--sika-font-sans)",
            color: isWin ? "var(--sika-positive, #27AE60)" : "var(--sika-negative, #C0392B)",
          }}>
            {isWin ? (d.completed ? "Chain Complete!" : "Week Won") : "Chain Busted"}
          </div>
          <div className="meta">
            {isWin
              ? `New value: GH₵ ${fmtAmt(d.new_value)}`
              : `Protocol: ${d.bust_protocol ?? "stop"}`}
          </div>
        </div>
      </div>
    );
  }

  // Multi-chain list
  if (data && typeof data === "object" && "chains" in data) {
    const chains = ((data as { chains: ChainData[] }).chains ?? []);
    return (
      <div className="plan-card">
        <div className="plan-head">
          <div className="label">ROLLOVER Chains</div>
          <div className="ttl" style={{ fontFamily: "var(--sika-font-sans)", fontSize: "var(--sika-text-lg)" }}>
            {chains.length} chain{chains.length !== 1 ? "s" : ""}
          </div>
        </div>
        {chains.map((c, i) => (
          <div key={c.id ?? i} style={{ borderTop: i > 0 ? "1px solid var(--sika-border)" : "none", paddingTop: i > 0 ? 12 : 0 }}>
            <div style={{ fontSize: "var(--sika-text-sm)", color: "var(--sika-text-muted)", marginBottom: 4 }}>
              Chain {(c.id ?? "").slice(0, 8)}...
            </div>
            <SingleChain c={c} />
          </div>
        ))}
      </div>
    );
  }

  // Single chain
  const c = (data ?? {}) as ChainData;
  return (
    <div className="plan-card">
      <div className="plan-head">
        <div className="label">Chain Status</div>
        <div className="ttl" style={{ fontFamily: "var(--sika-font-sans)", fontSize: "var(--sika-text-lg)" }}>
          {c.id ? `Chain ${c.id.slice(0, 8)}...` : "Chain"}
        </div>
        {c.sika_goal_id && (
          <div className="meta">
            <Icon name="link" size={10} /> Linked to goal
          </div>
        )}
      </div>
      <SingleChain c={c} />
    </div>
  );
}
